import {
  useLayoutEffect, useMemo, useRef, useState,
} from 'react';
import { createPortal } from 'react-dom';

// Utils
import cn from '../../../../utils/cn';

// Hook
import { useTimeField } from './useTimeField';
import { getQuarterHourOptions, parseTimeInput } from './timeFieldUtils';

// Components
import MenuItem from '../../../atoms/MenuItem/MenuItem';

// Styles
import styles from './TimeField.module.css';

const QUARTER_HOUR_MENU_MAX_HEIGHT = 200;
const QUARTER_HOUR_MENU_GAP = 4;
/** Table cells are often narrower than HH:mm + padding. */
const QUARTER_HOUR_MENU_MIN_WIDTH = 72;
/** Same escape hatch as Select's portaled menu (table header is z-index 100). */
const QUARTER_HOUR_MENU_Z_INDEX = 9999;

/**
 * Fixed position for the portaled quarter-hour menu, anchored to the input.
 * Opens upward when the viewport has more room above than below.
 * @param {DOMRect} rect - Input bounding rect
 * @returns {React.CSSProperties}
 */
function getQuarterHourMenuStyle(rect) {
  const spaceBelow = window.innerHeight - rect.bottom;
  const spaceAbove = rect.top;
  const openAbove = spaceBelow < QUARTER_HOUR_MENU_MAX_HEIGHT && spaceAbove > spaceBelow;

  return {
    position: 'fixed',
    top: openAbove ? rect.top - QUARTER_HOUR_MENU_GAP : rect.bottom + QUARTER_HOUR_MENU_GAP,
    left: rect.left,
    width: Math.max(rect.width, QUARTER_HOUR_MENU_MIN_WIDTH),
    minWidth: QUARTER_HOUR_MENU_MIN_WIDTH,
    maxHeight: QUARTER_HOUR_MENU_MAX_HEIGHT,
    zIndex: QUARTER_HOUR_MENU_Z_INDEX,
    transform: openAbove ? 'translateY(-100%)' : undefined,
  };
}

/**
 * TimeField - Input with time entry assistance
 * @param {object} props
 * @param {string} [props.value] - Value in HH:mm format
 * @param {(value: string) => void} props.onChange - Change handler that receives HH:mm format
 * @param {(e: React.FocusEvent<HTMLInputElement>) => void} [props.onBlur] - Blur handler
 * @param {(e: React.KeyboardEvent<HTMLInputElement>) => void} [props.onKeyDown] - Key down handler
 * @param {string} [props.name] - Input name attribute
 * @param {boolean} [props.disabled] - Whether input is disabled
 * @param {boolean} [props.readOnly] - Read-only (posted, not user-editable)
 * @param {boolean} [props.required] - Whether input is required
 * @param {string} [props.error] - Validation error
 * @param {string} [props.className] - Additional CSS classes
 * @param {'default' | 'light' | 'table'} [props.theme = 'default'] - Theme of the time field
 * @param {string} [props.placeholder] - Placeholder text
 * @param {boolean} [props.withQuarterHourDropdown=false] - Show quarter-hour dropdown (direct-edit)
 * @param {(value: string) => void} [props.onQuarterHourSelect] - Commit handler when a quarter-hour is picked (e.g. table direct-edit save)
 * @param {(value: string) => void} [props.onValidatedCommit] - Commit validated HH:mm (or empty) on blur/Enter (e.g. direct-edit save)
 * @param {() => void} [props.onCommitInvalid] - Called when blur/Enter cannot resolve a valid time
 * @returns {JSX.Element} TimeField component
 */
function TimeField({
  value = '',
  onChange,
  onBlur,
  onKeyDown,
  name,
  disabled = false,
  readOnly = false,
  required = false,
  error = '',
  className = '',
  theme = 'default',
  placeholder = 'HH:MM',
  withQuarterHourDropdown = false,
  onQuarterHourSelect,
  onValidatedCommit,
  onCommitInvalid,
}) {
  const inputRef = useRef(/** @type {HTMLInputElement | null} */ (null));
  const menuRef = useRef(/** @type {HTMLDivElement | null} */ (null));
  const skipNextBlurSaveRef = useRef(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState(/** @type {React.CSSProperties | null} */ (null));
  const quarterHourOptions = useMemo(
    () => (withQuarterHourDropdown ? getQuarterHourOptions() : []),
    [withQuarterHourDropdown],
  );

  const {
    displayValue,
    isEmpty,
    handleChange,
    handleBlur,
    handleKeyDown,
    selectOption,
  } = useTimeField(value, onChange);

  /**
   * Resolve display value for direct-edit commit (blur / Enter).
   * @returns {'empty' | { validated: string } | 'invalid'} Resolution for commit
   */
  const resolveCommit = () => {
    if (!displayValue.trim()) return 'empty';
    const parsed = parseTimeInput(displayValue);
    if (parsed === null) return 'invalid';
    return { validated: parsed };
  };

  /**
   * Commit validated time for direct-edit when configured.
   * @param {'blur' | 'enter'} source - Event source
   * @returns {boolean} True when commit path handled the event
   */
  const commitValidatedValue = (source) => {
    if (!onValidatedCommit || readOnly) return false;

    const resolution = resolveCommit();
    if (resolution === 'invalid') {
      onCommitInvalid?.();
      return true;
    }

    if (resolution === 'empty') {
      onChange('');
      onValidatedCommit('');
      return true;
    }

    selectOption(resolution.validated);
    onValidatedCommit(resolution.validated);
    if (source === 'enter') {
      skipNextBlurSaveRef.current = true;
    }
    return true;
  };

  /**
   * Handle input blur event
   * @param {React.FocusEvent<HTMLInputElement>} e
   */
  const handleInputBlur = (e) => {
    e.preventDefault();
    if (skipNextBlurSaveRef.current) {
      skipNextBlurSaveRef.current = false;
      if (withQuarterHourDropdown) {
        setIsDropdownOpen(false);
      }
      return;
    }
    if (commitValidatedValue('blur')) {
      if (withQuarterHourDropdown) {
        setIsDropdownOpen(false);
      }
      return;
    }
    if (!readOnly) {
      handleBlur();
    }
    onBlur?.(e);
    if (withQuarterHourDropdown) {
      setIsDropdownOpen(false);
    }
  };

  /**
   * @param {React.FocusEvent<HTMLInputElement>} event
   */
  const handleInputFocus = (event) => {
    if (theme !== 'table') {
      event.currentTarget.select();
    }
    if (withQuarterHourDropdown && !readOnly) {
      setIsDropdownOpen(true);
    }
  };

  useLayoutEffect(() => {
    if (!withQuarterHourDropdown || !isDropdownOpen || disabled) {
      setMenuStyle(null);
      return undefined;
    }

    const updateMenuStyle = () => {
      const input = inputRef.current;
      if (!input) return;
      setMenuStyle(getQuarterHourMenuStyle(input.getBoundingClientRect()));
    };

    /**
     * Reposition when the table scrolls. Ignore scroll inside the menu itself.
     * @param {Event} event
     */
    const handleScroll = (event) => {
      const { target } = event;
      if (target instanceof Node && menuRef.current?.contains(target)) return;
      updateMenuStyle();
    };

    updateMenuStyle();
    window.addEventListener('resize', updateMenuStyle);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      window.removeEventListener('resize', updateMenuStyle);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [disabled, isDropdownOpen, withQuarterHourDropdown]);

  /**
   * @param {string} optionValue
   */
  const handleSelectOption = (optionValue) => {
    const parsed = parseTimeInput(optionValue);
    if (!parsed) return;

    selectOption(optionValue);
    setIsDropdownOpen(false);

    if (onQuarterHourSelect) {
      skipNextBlurSaveRef.current = true;
      onQuarterHourSelect(parsed);
      return;
    }

    inputRef.current?.focus();
  };

  /**
   * Handle key down event
   * @param {React.KeyboardEvent<HTMLInputElement>} e
   */
  const handleInputKeyDown = (e) => {
    if (onValidatedCommit && e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      commitValidatedValue('enter');
      return;
    }
    if (onCommitInvalid && e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      onCommitInvalid();
      return;
    }
    if (!readOnly) {
      handleKeyDown(e);
    }
    onKeyDown?.(e);
  };

  /**
   * @param {React.ChangeEvent<HTMLInputElement>} e
   */
  const handleInputChange = (e) => {
    if (readOnly) return;
    handleChange(e);
  };

  return (
    <div className={cn([styles.wrapper, error && styles['has-error'], className])}>
      <input
        ref={inputRef}
        type="text"
        name={name}
        value={displayValue}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        onKeyDown={handleInputKeyDown}
        disabled={disabled}
        readOnly={readOnly}
        required={required}
        placeholder={placeholder}
        className={cn([
          styles.input,
          styles[theme],
          isEmpty && styles['input-empty'],
          disabled && styles['input-disabled'],
          readOnly && styles['input-read-only'],
          error && styles['input-error'],
        ])}
        role={withQuarterHourDropdown ? 'combobox' : undefined}
        aria-expanded={withQuarterHourDropdown ? isDropdownOpen : undefined}
        aria-haspopup={withQuarterHourDropdown ? 'true' : undefined}
      />

      {withQuarterHourDropdown && isDropdownOpen && !disabled && menuStyle
        ? createPortal(
          // Prevent input blur before quarter-hour option click is handled.
          // The menu is portaled so it is not clipped by table cell stacking.
          // eslint-disable-next-line jsx-a11y/no-static-element-interactions
          <div
            ref={menuRef}
            className={styles.dropdown}
            style={menuStyle}
            onMouseDown={(e) => e.preventDefault()}
          >
            {quarterHourOptions.map((option) => (
              <MenuItem
                key={option}
                className={styles['dropdown-item']}
                isActive={option === displayValue}
                onClick={() => handleSelectOption(option)}
              >
                {option}
              </MenuItem>
            ))}
          </div>,
          document.body,
        )
        : null}

      {error && (
        <div className={styles.error} role="alert">
          {error}
        </div>
      )}
    </div>
  );
}

export default TimeField;
